import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Button, Slider, Typography } from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  LeftOutlined,
  RightOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import * as echarts from 'echarts';
import {
  type VariableKey,
  type CityForecast,
  type PrefectureName,
  PREFECTURE_GEO,
  getCounties,
} from '@/api/weatherMap';

const { Text } = Typography;

const VAR_COLORS: Record<VariableKey, [string, string]> = {
  ghi: ['#f5f5f0', '#e87c20'],
  cloud: ['#fafafa', '#4a4a4a'],
  wind: ['#e8f4fd', '#1a5fa8'],
  precip: ['#e8f8e8', '#1a7a1a'],
};

interface CountyHover {
  name: string;
  ghi: number | null;
  cloud: number | null;
  wind: number | null;
  precip: number | null;
  x: number;
  y: number;
}

interface Props {
  open: boolean;
  prefecture: PrefectureName | null;
  allCities: CityForecast[];
  hour: number;
  variable: VariableKey;
  provinceMax: number;
  onClose: () => void;
  onHourChange: (h: number) => void;
  onPlayingChange: (p: boolean) => void;
  playing: boolean;
}

const WeatherMapModal: React.FC<Props> = ({
  open,
  prefecture,
  allCities,
  hour,
  variable,
  provinceMax,
  onClose,
  onHourChange,
  onPlayingChange,
  playing,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [geoLoaded, setGeoLoaded] = useState(false);
  const [hoveredCounty, setHoveredCounty] = useState<CountyHover | null>(null);

  const geoKey = prefecture ? PREFECTURE_GEO[prefecture] : null;

  // 加载该地市的县级 GeoJSON
  useEffect(() => {
    if (!geoKey) return;
    setGeoLoaded(false);
    fetch(`/geo/${geoKey}.json`)
      .then((r) => r.json())
      .then((geo) => {
        echarts.registerMap(geoKey, geo);
        setGeoLoaded(true);
      })
      .catch(console.error);
  }, [geoKey]);

  // 该地市下辖县数据
  const countyData = useMemo(() => {
    if (!prefecture) return [];
    return getCounties(allCities, prefecture);
  }, [allCities, prefecture]);

  // series data：用 GeoJSON 短名匹配
  const seriesData = useMemo(() => {
    if (!prefecture) return [];
    return countyData.map((c) => {
      const shortName = c.name.replace(prefecture, '');
      const hd = c.hours[hour];
      return {
        name: shortName,
        value: hd?.[variable] ?? undefined,
        ghi: hd?.ghi ?? null,
        cloud: hd?.cloud ?? null,
        wind: hd?.wind ?? null,
        precip: hd?.precip ?? null,
        fullName: c.name,
      };
    });
  }, [countyData, hour, variable, prefecture]);

  // 渲染地图
  useEffect(() => {
    if (!open || !chartRef.current || !geoLoaded || !geoKey || seriesData.length === 0) return;

    if (chartInstance.current) chartInstance.current.dispose();
    const chart = echarts.init(chartRef.current);
    chartInstance.current = chart;

    const [colorLow, colorHigh] = VAR_COLORS[variable];

    chart.setOption({
      tooltip: { show: false },
      visualMap: {
        min: 0,
        max: provinceMax > 0 ? provinceMax : 100,
        inRange: { color: [colorLow, colorHigh] },
        show: false,
      },
      series: [
        {
          type: 'map',
          map: geoKey,
          roam: false,
          label: { show: true, color: '#333', fontSize: 12 },
          itemStyle: {
            areaColor: '#f0f0f0',
            borderColor: '#fff',
            borderWidth: 1.5,
          },
          emphasis: {
            label: { show: true, fontWeight: 'bold' },
            itemStyle: { areaColor: '#ffd591' },
          },
          data: seriesData,
        },
      ],
    });

    chart.off('mousemove');
    chart.on('mousemove', (params: unknown) => {
      const p = params as { name?: string; event?: { offsetX?: number; offsetY?: number } };
      if (p.name) {
        const d = seriesData.find((s) => s.name === p.name);
        if (d) {
          setHoveredCounty({
            name: d.fullName,
            ghi: d.ghi,
            cloud: d.cloud,
            wind: d.wind,
            precip: d.precip,
            x: p.event?.offsetX ?? 0,
            y: p.event?.offsetY ?? 0,
          });
          return;
        }
      }
      setHoveredCounty(null);
    });
    chart.on('mouseout', () => setHoveredCounty(null));

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [open, geoLoaded, seriesData, variable, provinceMax, geoKey]);

  // 关闭清理
  useEffect(() => {
    if (!open) {
      setHoveredCounty(null);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    }
  }, [open]);

  if (!open || !prefecture) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.35)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
          width: 'min(90vw, 720px)',
          maxHeight: '90vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={onClose}>
            返回全省
          </Button>
          <Text strong style={{ flex: 1, textAlign: 'center', fontSize: 16 }}>
            {prefecture}
          </Text>
        </div>

        {/* 地图区域 */}
        <div style={{ position: 'relative', padding: '8px 16px' }}>
          <div ref={chartRef} style={{ width: '100%', height: 380 }} />

          {/* 县级悬浮卡片 */}
          {hoveredCounty && (
            <div
              style={{
                position: 'absolute',
                left: Math.min(hoveredCounty.x + 16, 540),
                top: Math.min(hoveredCounty.y + 12, 300),
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 8,
                padding: '10px 14px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                fontSize: 13,
                lineHeight: 1.8,
                zIndex: 10,
                pointerEvents: 'none',
                minWidth: 150,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 2, fontSize: 14 }}>
                {hoveredCounty.name}
              </div>
              <div>
                ☀ 辐照{' '}
                {hoveredCounty.ghi !== null ? `${hoveredCounty.ghi} W/m²` : '无数据'}
              </div>
              <div>
                ☁ 云量 {hoveredCounty.cloud !== null ? `${hoveredCounty.cloud}%` : '无数据'}
              </div>
              <div>
                💨 风速{' '}
                {hoveredCounty.wind !== null ? `${hoveredCounty.wind} km/h` : '无数据'}
              </div>
              <div>
                🌧 降水{' '}
                {hoveredCounty.precip !== null ? `${hoveredCounty.precip} mm` : '无数据'}
              </div>
            </div>
          )}
        </div>

        {/* 时间轴 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 20px',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Button
            size="small"
            icon={<LeftOutlined />}
            disabled={hour === 0}
            onClick={() => onHourChange(Math.max(0, hour - 1))}
          />
          <Button
            size="small"
            icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => onPlayingChange(!playing)}
          />
          <Slider
            style={{ flex: 1 }}
            min={0}
            max={23}
            value={hour}
            onChange={(v) => onHourChange(v)}
            marks={{
              0: '00时',
              6: '06时',
              12: '12时',
              18: '18时',
              23: '23时',
            }}
            tooltip={{ formatter: (v) => `${v}时` }}
          />
          <Button
            size="small"
            icon={<RightOutlined />}
            disabled={hour === 23}
            onClick={() => onHourChange(Math.min(23, hour + 1))}
          />
          <Text strong style={{ minWidth: 48, textAlign: 'center' }}>
            {hour}时
          </Text>
        </div>
      </div>
    </div>
  );
};

export default WeatherMapModal;
